import React from 'react';
import type { FileRejection, FileWithPath } from '@mantine/dropzone';
import {
  IconCircleCheck,
  IconFileAlert,
  IconFileUpload,
} from '@tabler/icons-react';
import { AttachmentDto } from 'src/api/FiretrolApiClient';
import { Alert } from 'src/design/atoms/Alert';
import { Badge } from 'src/design/atoms/Badge';
import { BasicButton } from 'src/design/atoms/BasicButton';
import { Box } from 'src/design/atoms/Box';
import { DownloadActionIcon } from 'src/design/atoms/DownloadActionIcon';
import { Group } from 'src/design/atoms/Group';
import { Input } from 'src/design/atoms/Input';
import { Loader } from 'src/design/atoms/Loader';
import { Paper } from 'src/design/atoms/Paper';
import { SecondaryButton } from 'src/design/atoms/SecondaryButton';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { Tooltip } from 'src/design/atoms/Tooltip';
import { DropPrompt } from 'src/design/molecules/DropPrompt';
import { UploadProblemAlert } from 'src/design/molecules/UploadProblemAlert';
import { Dropzone } from 'src/design/organisms/Dropzone';
import { useAttachmentUpload } from 'src/design/organisms/attachments/useAttachmentUpload';
import { formatDate, humanFileSize } from 'src/utils';
import {
  DesignModelFileKind,
  FILE_KIND_FEEDS,
  FILE_KIND_LABELS,
  ISlotView,
  deriveSlotState,
  extensionOf,
  wrongTypeMessage,
} from 'src/routes/Jobs/DesignModels/designModelFiles';

const WRONG_TYPE = 'wrong-file-type';

export const FAB_REPLACEMENT_NOTE =
  'New FAB levels are added under the WBS root. Accounts from the previous FAB file are not removed.';

export interface IDesignModelFileSlotProps {
  kind: DesignModelFileKind;
  saved?: AttachmentDto;
  staged?: AttachmentDto;
  /** The server reports this file's blob (or its parsed data) as gone from storage. */
  missing: boolean;
  lockedReason?: string | null;
  error?: string | null;
  uploadFile: (file: File) => Promise<AttachmentDto>;
  downloadFile: (id: string) => Promise<{ url: string; name: string }>;
  onStage: (kind: DesignModelFileKind, file: AttachmentDto) => void;
  onUnstage: (kind: DesignModelFileKind) => void;
  onUploadingChange: (kind: DesignModelFileKind, uploading: boolean) => void;
}

const LockTooltip: React.FC<{
  reason: string | null;
  children: React.ReactNode;
}> = ({ reason, children }) => (
  <Tooltip label={reason} disabled={!reason}>
    <Box component="span" display="inline-block">
      {children}
    </Box>
  </Tooltip>
);

const stateIcon = (view: ISlotView) => {
  switch (view.state) {
    case 'uploading':
      return <Loader size={14} />;
    case 'stagedNew':
    case 'stagedReplacement':
      return <IconFileUpload size={18} color="var(--mantine-color-yellow-7)" />;
    case 'savedMissing':
      return <IconFileAlert size={18} color="var(--mantine-color-red-7)" />;
    default:
      return <IconCircleCheck size={18} color="var(--mantine-color-green-7)" />;
  }
};

export const DesignModelFileSlot: React.FC<IDesignModelFileSlotProps> = ({
  kind,
  saved,
  staged,
  missing,
  lockedReason,
  error,
  uploadFile,
  downloadFile,
  onStage,
  onUnstage,
  onUploadingChange,
}) => {
  const label = FILE_KIND_LABELS[kind];
  const openFilePicker = React.useRef<() => void>(null);
  const [pendingFile, setPendingFile] = React.useState<File | null>(null);
  const [typeRejections, setTypeRejections] = React.useState<string[]>([]);

  const upload = useAttachmentUpload({
    uploadFile,
    downloadFile,
    onUploaded: (uploaded) => onStage(kind, uploaded[0] as AttachmentDto),
  });

  React.useEffect(() => {
    onUploadingChange(kind, upload.loading);
  }, [kind, upload.loading, onUploadingChange]);

  const problems = [...typeRejections, ...upload.problems];
  const view = deriveSlotState({
    saved,
    staged,
    missing,
    uploading: upload.loading,
    lockedReason,
    error,
    problems,
  });

  const validator = (file: File) =>
    extensionOf(file.name) === kind
      ? null
      : { code: WRONG_TYPE, message: wrongTypeMessage(file.name, kind) };

  const handleDrop = (files: FileWithPath[]) => {
    setTypeRejections([]);
    setPendingFile(files[0] ?? null);
    return upload.onDrop(files);
  };

  const handleReject = (rejections: FileRejection[]) => {
    const isWrongType = (r: FileRejection) =>
      r.errors.some((e) => e.code === WRONG_TYPE);
    const wrongType = rejections.filter(isWrongType);
    const other = rejections.filter((r) => !isWrongType(r));
    setTypeRejections(
      wrongType.map(
        (r) => r.errors.find((e) => e.code === WRONG_TYPE)!.message,
      ),
    );
    if (other.length > 0) upload.onReject(other);
  };

  const clearProblems = () => {
    setTypeRejections([]);
    upload.clearProblems();
  };

  const dropzoneProps = {
    onDrop: handleDrop,
    onReject: handleReject,
    validator,
    multiple: false,
    maxFiles: 1,
    useFsAccessApi: false,
    openRef: openFilePicker,
    disabled: view.locked,
  };

  const metaLine = (): React.ReactNode => {
    switch (view.state) {
      case 'uploading':
        return `Uploading · ${humanFileSize(pendingFile?.size)}`;
      case 'stagedNew':
        return `${humanFileSize(staged?.size)} · Uploaded just now`;
      case 'stagedReplacement':
        return (
          <>
            Replaces{' '}
            <Text component="span" inherit td="line-through">
              {saved?.name}
            </Text>
            {missing ? ' (missing)' : ''} on save
          </>
        );
      case 'savedMissing':
        return 'This file is no longer in storage. Upload it again and save to restore processing.';
      default:
        return `${humanFileSize(saved?.size)} · Uploaded ${formatDate(
          saved?.createdAt,
          'MMM D, YYYY',
        )}`;
    }
  };

  const fileName =
    view.state === 'uploading'
      ? pendingFile?.name
      : view.state === 'stagedNew' || view.state === 'stagedReplacement'
      ? staged?.name
      : saved?.name;

  const replaceButtonProps = {
    size: 'xs' as const,
    'aria-label': `Replace ${label} file`,
    disabled: !view.canReplace,
    onClick: () => openFilePicker.current?.(),
    children: 'Replace',
  };

  return (
    <Input.Wrapper
      label={`${label} File`}
      labelElement="div"
      required
      description={FILE_KIND_FEEDS[kind]}
      error={view.error}
    >
      <Stack gap="xs" mt={4}>
        {view.state === 'empty' ? (
          <Dropzone
            {...dropzoneProps}
            aria-label={`Add ${label} file`}
            style={
              view.error
                ? { borderColor: 'var(--mantine-color-error)' }
                : undefined
            }
          >
            <DropPrompt
              title={`Drop the .${kind} file here or click to browse`}
              description={view.lockedReason ?? 'Required'}
            />
          </Dropzone>
        ) : (
          <>
            <Dropzone
              {...dropzoneProps}
              display="none"
              unstyled
              activateOnClick={false}
            >
              <Box component="span" />
            </Dropzone>
            <Paper withBorder radius="sm" p="sm">
              <Group justify="space-between" align="flex-start" gap="xs">
                <Group
                  gap="xs"
                  align="flex-start"
                  wrap="nowrap"
                  miw={0}
                  style={{ flex: '1 1 220px' }}
                >
                  <Box pt={2}>{stateIcon(view)}</Box>
                  <Stack gap={2} miw={0}>
                    <Text size="sm" c="bright" fw={500} truncate>
                      {fileName}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {metaLine()}
                    </Text>
                  </Stack>
                </Group>
                <Group gap="xs" wrap="nowrap" style={{ flexShrink: 0 }}>
                  {(view.state === 'stagedNew' ||
                    view.state === 'stagedReplacement') && (
                    <Badge color="yellow" variant="light">
                      Not saved
                    </Badge>
                  )}
                  {view.state === 'savedMissing' && (
                    <Badge color="red" variant="light">
                      Missing
                    </Badge>
                  )}
                  {view.canDownload && saved && (
                    <DownloadActionIcon
                      aria-label={`Download ${label} file`}
                      variant="subtle"
                      iconSize={18}
                      onClick={() => upload.download(saved)}
                    />
                  )}
                  {view.state !== 'uploading' && (
                    <LockTooltip reason={view.lockedReason}>
                      {view.state === 'savedMissing' ? (
                        <BasicButton
                          variant="light"
                          color="red"
                          {...replaceButtonProps}
                        />
                      ) : (
                        <SecondaryButton {...replaceButtonProps} />
                      )}
                    </LockTooltip>
                  )}
                  {view.state === 'stagedReplacement' && (
                    <LockTooltip reason={view.lockedReason}>
                      <BasicButton
                        variant="subtle"
                        size="xs"
                        aria-label={`Undo ${label} replacement`}
                        disabled={!view.canUndo}
                        onClick={() => onUnstage(kind)}
                      >
                        Undo
                      </BasicButton>
                    </LockTooltip>
                  )}
                  {view.state === 'stagedNew' && (
                    <LockTooltip reason={view.lockedReason}>
                      <BasicButton
                        variant="subtle"
                        size="xs"
                        aria-label={`Remove ${label} file`}
                        disabled={!view.canRemove}
                        onClick={() => onUnstage(kind)}
                      >
                        Remove
                      </BasicButton>
                    </LockTooltip>
                  )}
                </Group>
              </Group>
              {kind === 'fab' && view.state === 'stagedReplacement' && (
                <Alert color="yellow" mt="xs">
                  {FAB_REPLACEMENT_NOTE}
                </Alert>
              )}
            </Paper>
          </>
        )}
        <UploadProblemAlert problems={problems} onClear={clearProblems} />
      </Stack>
    </Input.Wrapper>
  );
};
